import React from "react";
import { FileText, Landmark, Mail } from "lucide-react";
import { Link } from "@/interfaces/router";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/components/utils";
import InvestmentAvatar from "@/components/investmentAvatar";
import {
  getCompanyUrlAliasFromUrl,
  useCompanyIdFromUrl,
} from "@/hooks/companies/useCompanyIdFromUrl";

const entityReferencePresentation = {
  document: {
    Icon: FileText,
    fallbackLabel: "Document",
  },
  investment: {
    Icon: Landmark,
    fallbackLabel: "Shareholder",
  },
  userComm: {
    Icon: Mail,
    fallbackLabel: "Communication message",
  },
};

const isEntityType = (type) =>
  Object.prototype.hasOwnProperty.call(entityReferencePresentation, type);

const isEntityReference = (entityReference) =>
  !!entityReference &&
  typeof entityReference.id === "string" &&
  typeof entityReference.companyId === "string" &&
  (entityReference.label === undefined ||
    typeof entityReference.label === "string") &&
  isEntityType(entityReference.type);

// Mirrors parseEntityUri in @invono/shared/contracts/entities/v1.
const parseEntityUri = (uri) => {
  const match = /^invono:\/\/([^/?#]+)\/([^/?#]+)$/.exec(uri ?? "");
  if (!match || !isEntityType(match[1])) {
    return null;
  }

  try {
    return { type: match[1], id: decodeURIComponent(match[2]) };
  } catch {
    return null;
  }
};

const getEntityReferenceHref = (entityReference, companyUrlAlias) => {
  switch (entityReference.type) {
    case "document":
      return `/${companyUrlAlias}/documents/${entityReference.id}`;
    case "investment":
      return `/${companyUrlAlias}/shares/register-of-shareholders/${entityReference.id}`;
    case "userComm":
      return `/${companyUrlAlias}/communication-center?filterBy=${encodeURIComponent(
        entityReference.id,
      )}`;
  }
};

const EntityReferenceLink = ({
  entityReference,
  appearance = "button",
  children,
}) => {
  const currentCompanyId = useCompanyIdFromUrl();
  const companyUrlAlias = getCompanyUrlAliasFromUrl();

  if (
    !isEntityReference(entityReference) ||
    !companyUrlAlias ||
    currentCompanyId !== entityReference.companyId
  ) {
    return appearance === "inline" ? (
      <span>{children ?? entityReference?.label}</span>
    ) : null;
  }

  const { Icon, fallbackLabel } =
    entityReferencePresentation[entityReference.type];
  const href = getEntityReferenceHref(entityReference, companyUrlAlias);
  const label = children ?? (entityReference.label || fallbackLabel);

  const isInvestment = entityReference.type === "investment";

  if (appearance === "inline") {
    return (
      <Link
        to={href}
        className="text-primary inline-flex items-baseline gap-1 font-medium underline-offset-4 hover:underline"
      >
        {isInvestment ? (
          <InvestmentAvatar
            investmentId={entityReference.id}
            companyId={entityReference.companyId}
            investorName={
              typeof label === "string" ? label : entityReference.label
            }
            size="xs"
            className="self-center"
          />
        ) : (
          <Icon size={14} className="shrink-0 self-center" />
        )}
        {label}
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className={cn(
        buttonVariants({ variant: "secondary", size: "sm" }),
        "gap-2",
        { "pl-1": isInvestment },
      )}
    >
      {isInvestment ? (
        <InvestmentAvatar
          investmentId={entityReference.id}
          companyId={entityReference.companyId}
          investorName={entityReference.label}
          size="sm"
        />
      ) : (
        <Icon size={16} />
      )}
      {label}
    </Link>
  );
};

// Inline invono:// links omit companyId; they always refer to the current company.
const EntityUriLink = ({ uri, children }) => {
  const currentCompanyId = useCompanyIdFromUrl();
  const parsedUri = parseEntityUri(uri);
  const entityReference =
    parsedUri && currentCompanyId
      ? { ...parsedUri, companyId: currentCompanyId }
      : null;

  return (
    <EntityReferenceLink entityReference={entityReference} appearance="inline">
      {children}
    </EntityReferenceLink>
  );
};

export {
  EntityReferenceLink,
  EntityUriLink,
  getEntityReferenceHref,
  parseEntityUri,
};
